import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import IconButton from '@mui/material/IconButton'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CloseIcon from '@mui/icons-material/Close'
import Chip from '@mui/material/Chip'
import styled from 'styled-components'
import Typography from '@/v2/components/common/Typography'
import PatientAvatar from '@/v2/components/common/PatientAvatar'
import Accordion from '@/v2/components/common/Accordion'
import { MessagesProvider } from '@/v2/contexts/MessagesContext'
import {
  useConversation,
  useUpdateConversation,
} from '@/v2/hooks/messages/useConversations'
import { formatPhone } from '@/v2/utils/format'
import MessagesHistory from '../MessagesHistory'
import ContactInfo from '../ContactInfo'
import Appointments from '../Appointments'
import ConversationActions from '../PatientHeader/ConversationActions'
import {
  MessagesWrapper,
  PatientHeaderWrapper,
  PatientHeaderInfo,
  PatientName,
  MessagesContent,
  MessagesHistoryWrapper,
  RightSidebar,
  PatientInfoButton,
  MobilePatientInfoOverlay,
  MobilePatientInfoPanel,
  MobilePatientInfoHeader,
  EmptyState,
} from './styles'

const AccordionStyled = styled(Accordion)`
  margin: 0 10px;
  width: calc(100% - 20px);
`

const LoadingWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`

const MobileMessagesPage = () => {
  const theme = useTheme()
  const { conversationId } = useParams()
  const { conversation = {}, isLoading } = useConversation(conversationId)
  const { read = true, messages = {} } = conversation
  const { records, currentPage, totalPages } = messages
  const { updateConversation } = useUpdateConversation(conversationId)
  const [showPatientInfo, setShowPatientInfo] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!read) {
      const setReadMessage = async () => {
        if (!cancelled) {
          await updateConversation({ read: true })
        }
      }
      setReadMessage()
    }
    return () => {
      cancelled = true
    }
  }, [read, updateConversation])

  if (isLoading && !!conversationId) {
    return (
      <MessagesWrapper>
        <LoadingWrapper>
          <Typography>Loading...</Typography>
        </LoadingWrapper>
      </MessagesWrapper>
    )
  }

  const patients = conversation?.patients || []
  const [firstPatient] = patients || []
  const patient = {
    patient: firstPatient || {},
    phoneNumber: conversation.phoneNumber,
  }

  const patientNames = patients
    .map(p => p.fullName || formatPhone(p.phoneNumber))
    .join(', ')

  const displayName =
    patientNames || formatPhone(conversation.phoneNumber) || 'Unknown'

  return (
    <MessagesProvider patient={patient}>
      <MessagesWrapper>
        <PatientHeaderWrapper>
          <PatientHeaderInfo>
            <PatientAvatar
              patient={
                firstPatient || { phoneNumber: conversation.phoneNumber }
              }
              size={40}
              sx={{ width: 40, height: 40, flexShrink: 0 }}
            />
            <PatientName>
              <Typography
                fontSize="16px"
                fontWeight="500"
                variant="h2"
                sx={{
                  margin: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayName}
              </Typography>
            </PatientName>
          </PatientHeaderInfo>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              flexShrink: 0,
            }}
          >
            <Chip
              label={conversation.open ? 'Open' : 'Closed'}
              size="small"
              sx={{ fontSize: '12px' }}
            />
            {!!conversation && (
              <ConversationActions conversation={conversation} />
            )}
            <PatientInfoButton onClick={() => setShowPatientInfo(true)}>
              <InfoOutlinedIcon />
            </PatientInfoButton>
          </div>
        </PatientHeaderWrapper>

        <MessagesContent>
          <MessagesHistoryWrapper>
            <MessagesHistory
              historyMessages={records}
              currentPage={currentPage}
              totalPages={totalPages}
            />
          </MessagesHistoryWrapper>

          <RightSidebar>
            {patients?.length > 0 ? (
              patients.map(p => (
                <AccordionStyled
                  noPaddingTitle
                  noBackground
                  key={p.id}
                  defaultExpanded={patients.length === 1}
                  title={<Typography>{p.fullName}</Typography>}
                  content={
                    <>
                      <ContactInfo patient={p} conversation={conversation} />
                      <Appointments patient={p} />
                    </>
                  }
                />
              ))
            ) : (
              <EmptyState>
                <Typography fontSize="14px" color="#666">
                  No patient assigned
                </Typography>
              </EmptyState>
            )}
          </RightSidebar>
        </MessagesContent>

        {showPatientInfo && (
          <MobilePatientInfoOverlay onClick={() => setShowPatientInfo(false)}>
            <MobilePatientInfoPanel onClick={e => e.stopPropagation()}>
              <MobilePatientInfoHeader>
                <Typography fontSize="16px" fontWeight="500">
                  Patient Info
                </Typography>
                <IconButton
                  onClick={() => setShowPatientInfo(false)}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </MobilePatientInfoHeader>
              {patients?.length > 0 ? (
                patients.map(p => (
                  <AccordionStyled
                    noPaddingTitle
                    noBackground
                    key={p.id}
                    defaultExpanded={patients.length === 1}
                    title={<Typography>{p.fullName}</Typography>}
                    content={
                      <>
                        <ContactInfo patient={p} conversation={conversation} />
                        <Appointments patient={p} />
                      </>
                    }
                  />
                ))
              ) : (
                <EmptyState>
                  <Typography fontSize="14px" color="#666">
                    No patient assigned to this conversation
                  </Typography>
                </EmptyState>
              )}
            </MobilePatientInfoPanel>
          </MobilePatientInfoOverlay>
        )}
      </MessagesWrapper>
    </MessagesProvider>
  )
}

export default MobileMessagesPage
